import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Chip
} from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Icon from "@mui/material/Icon";
import * as api from 'utils/api'; // Assuming api utility is in this path
import { formatDate, formatDateTime } from 'utils/dateFormatter'; // Assuming dateFormatter is in this path
import MDButton from 'components/MDButton';
import PageErrorAlert, { PAGE_ERROR_TITLES } from 'components/PageErrorAlert';
import MDBadge from 'components/MDBadge'; // Import MDBadge
import ContractDetailTwoColumnLayout from './ContractDetailTwoColumnLayout';
import GroupPolicyGate from 'components/GroupPolicyGate';
import PropTypes from 'prop-types';
import { fetchClientzoneContract } from 'utils/clientzonePortalApi';

// Constants for styling, similar to CustomerView
const CONTRACT_STATUS_COLORS = {
  active: "success",
  pending: "secondary",
  suspended: "error",
  terminated: "dark",
  expired: "warning",
};

const safeParseJson = (jsonString, fieldName) => {
  if (!jsonString) return null;
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    console.warn(`Failed to parse ${fieldName} JSON:`, e, jsonString);
    return null;
  }
};

const getContractTermEndDateIso = (startDateRaw, termMonthsRaw) => {
  const start = String(startDateRaw || '').trim();
  const term = Number(termMonthsRaw || 0);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || term < 1) return null;
  const startDate = new Date(`${start}T00:00:00`);
  if (Number.isNaN(startDate.getTime())) return null;
  return formatDate(new Date(startDate.getFullYear(), startDate.getMonth() + term, 0));
};

const normalizeIsoDate = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return "";
  const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
};

const toRowsArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const INTERNAL_ID_KEYS = new Set([
  'id',
  'item_id',
  'sunscription_item_id',
  'category_id',
  'subscription_plan_id',
  'selected_plan_id',
  'linked_sunscription_item_id',
]);

/** Plan JSON placeholders; real values come from RADIUS when items link to a group profile. */
const PLAN_SUPPRESS_KEYS = new Set(['speed', 'throttle']);

const ITEM_HIDDEN_KEYS = new Set([
  ...INTERNAL_ID_KEYS,
  'name',
  'is_matched_to_config',
]);

const getStockTypeLabel = (stockType) => {
  const normalized = Number(stockType || 0);
  if (normalized === 2) return 'Subscription';
  if (normalized === 1) return 'Hardware';
  return 'Service Fee';
};

const toLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();

const formatFieldValue = (key, value) => {
  if (key === 'is_stock') return getStockTypeLabel(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return 'N/A';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

/**
 * Mikrotik-Rate-Limit shape: "2048K/4096K 5120K/10240K ..." — first token pair is throttle UL/DL, second is speed UL/DL.
 */
const parseMikrotikRateLimit = (rateLimit) => {
  const s = String(rateLimit || '').trim().replace(/^['"]+|['"]+$/g, '');
  if (!s) return null;
  const parts = s.split(/[\s,;]+/).map((p) => p.replace(/^['"]+|['"]+$/g, '')).filter(Boolean);
  if (parts.length >= 2) {
    return { throttleUlDl: parts[0], speedUlDl: parts[1] };
  }
  if (parts.length === 1 && parts[0].includes('/')) {
    return { throttleUlDl: parts[0], speedUlDl: parts[0] };
  }
  return null;
};

const MIKRO_RATE_TOKEN = /^(\d+(?:\.\d+)?)\s*([kKmMgG])$/;

/** Convert a single Mikrotik rate token (e.g. 2048K, 10M) to megabits per second (K uses ÷1024). */
const mikrotikTokenToMbps = (token) => {
  const raw = String(token || '').trim();
  if (!raw) return null;
  if (/^unlimited$/i.test(raw)) return null;
  const m = raw.match(MIKRO_RATE_TOKEN);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n)) return null;
  const suf = m[2].toUpperCase();
  if (suf === 'K') return n / 1024;
  if (suf === 'M') return n;
  if (suf === 'G') return n * 1024;
  return null;
};

const formatMbpsReadable = (mbps) => {
  if (!Number.isFinite(mbps) || mbps < 0) return '';
  const rounded = Math.round(mbps * 1000) / 1000;
  if (Math.abs(rounded - Math.round(rounded)) < 1e-6) return String(Math.round(rounded));
  return String(parseFloat(rounded.toFixed(3)));
};

/**
 * Display UL/DL pair in Mbps (K → Mbps via ÷1024; M as megabits/s; G → Mbps via ×1024).
 * Unparseable segments are left as the original token.
 */
const formatMikrotikUlDlMbps = (ulDl) => {
  const s = String(ulDl || '').trim();
  if (!s) return 'N/A';
  const halves = s.split('/').map((x) => x.trim());
  if (halves.length === 2) {
    const ulMbps = mikrotikTokenToMbps(halves[0]);
    const dlMbps = mikrotikTokenToMbps(halves[1]);
    const left = ulMbps != null ? `${formatMbpsReadable(ulMbps)} Mbps` : halves[0];
    const right = dlMbps != null ? `${formatMbpsReadable(dlMbps)} Mbps` : halves[1];
    return `${left} / ${right}`;
  }
  const one = mikrotikTokenToMbps(s);
  if (one != null) return `${formatMbpsReadable(one)} Mbps`;
  return s;
};

function ContractView({
  portalVariant = 'admin',
  expectedCustomerId = null,
  clientAccessToken = null,
}) {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isClientPortal = portalVariant === 'client';
  const wrapContractViewShell = (content) => {
    if (isClientPortal) {
      return content;
    }

    return (
      <DashboardLayout>
        <DashboardNavbar />
        {content}
      </DashboardLayout>
    );
  };
  // ParentViewReturnUrl is the URL that ContractView itself should use for its "Back" button.
  // This comes from whatever page navigated to ContractView.
  const parentViewReturnUrl = location.state?.returnUrl || (isClientPortal ? '/profile' : '/customers/list');
  const parentViewReturnState = isClientPortal
    ? { activeCustomerTab: location.state?.activeCustomerTab ?? 4 }
    : undefined;

  const [contractData, setContractData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scheduledTerminationDate, setScheduledTerminationDate] = useState('');
  const [hasTerminationSchedule, setHasTerminationSchedule] = useState(false);
  /** false = none / skip section; object = show RADIUS throttle & speed */
  const [radiusPackageInfo, setRadiusPackageInfo] = useState(null);

  useEffect(() => {
    const fetchContractDetails = async () => {
      if (!contractId) {
        setError("Contract ID is missing.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        if (isClientPortal) {
          if (!clientAccessToken || !expectedCustomerId) {
            throw new Error("Session expired. Please sign in again.");
          }

          const payload = await fetchClientzoneContract(contractId, clientAccessToken);
          const fetchedContract = payload?.contract;
          if (!fetchedContract) {
            throw new Error("Contract not found");
          }
          if (Number(fetchedContract.customer_id || 0) !== Number(expectedCustomerId)) {
            throw new Error("Contract not found");
          }

          setContractData(fetchedContract);
          setCustomerData(payload?.customer || null);
          setHasTerminationSchedule(Boolean(payload?.has_termination_schedule));
          setScheduledTerminationDate(String(payload?.scheduled_termination_date || "").trim());
          setError("");
          return;
        }

        const contractResponse = await api.post('/db/customer_contracts/filter', {
          filters: { contract_id: parseInt(contractId) },
          limit: 1,
        });

        if (!contractResponse || contractResponse.length === 0) {
          throw new Error('Contract not found');
        }
        const fetchedContract = contractResponse[0];
        setContractData(fetchedContract);
        const terminationRows = await api.post('/db/customer_contract_terminate/filter', {
          filters: { contract_id: parseInt(contractId, 10) },
          limit: 200,
        });
        const schedules = toRowsArray(terminationRows);
        const activeSchedules = schedules
          .filter((row) => Number(row?.is_terminated || 0) !== 1)
          .filter((row) => !['cancelled', 'terminated'].includes(String(row?.status || '').toLowerCase()))
          .map((row) => ({
            ...row,
            _scheduledDate: normalizeIsoDate(row?.scheduled_termination_date),
          }))
          .sort((a, b) =>
            String(b._scheduledDate || '').localeCompare(String(a._scheduledDate || ''))
          );
        setHasTerminationSchedule(activeSchedules.length > 0);
        const datedSchedules = activeSchedules.filter((row) => row._scheduledDate);
        setScheduledTerminationDate(
          datedSchedules.length > 0 ? String(datedSchedules[0]._scheduledDate || '').trim() : ''
        );

        if (fetchedContract.customer_id) {
          const customerResponse = await api.post('/db/customers/filter', {
            filters: { customer_id: fetchedContract.customer_id },
            limit: 1,
          });
          if (customerResponse && customerResponse.length > 0) {
            setCustomerData(customerResponse[0]);
          } else {
            console.warn("Customer not found for this contract.");
          }
        }
      } catch (err) {
        console.error('Error loading contract details:', err);
        setError(err.message || 'Failed to load contract details.');
      } finally {
        setLoading(false);
      }
    };

    fetchContractDetails();
  }, [contractId, isClientPortal, clientAccessToken, expectedCustomerId]);

  const parsedServiceData = useMemo(() => 
    contractData ? safeParseJson(contractData.service_data, 'service_data') : null,
    [contractData]
  );

  const parsedEquipmentData = useMemo(() =>
    contractData ? safeParseJson(contractData.equipment_data, 'equipment_data') : null,
    [contractData]
  );

  const parsedBillingData = useMemo(() =>
    contractData ? safeParseJson(contractData.billing_data, 'billing_data') : null,
    [contractData]
  );

  const serviceDataBreakdown = useMemo(() => {
    const serviceData = parsedServiceData || {};
    const additionalItems = Array.isArray(serviceData.additional_items) ? serviceData.additional_items : [];
    const subscriptionItems = Array.isArray(serviceData.subscription_items)
      ? serviceData.subscription_items
      : additionalItems.filter((item) => Number(item?.is_stock || 0) === 2);
    const hardwareItems = Array.isArray(serviceData.hardware_items)
      ? serviceData.hardware_items
      : additionalItems.filter(
          (item) => Number(item?.is_stock || 0) === 1 || Number(item?.is_own_equipment || 0) === 1
        );
    const serviceFeeItems = Array.isArray(serviceData.service_fee_items)
      ? serviceData.service_fee_items
      : additionalItems.filter((item) => Number(item?.is_stock || 0) === 0);

    const totals = serviceData.totals || {};
    const subscriptionTotal = Number(
      totals.subscription ??
        subscriptionItems.reduce((sum, item) => sum + Number(item?.actual_price_per_unit || 0), 0)
    );
    const hardwareTotal = Number(
      totals.hardware ??
        hardwareItems.reduce((sum, item) => sum + Number(item?.actual_price_per_unit || 0), 0)
    );
    const serviceFeesTotal = Number(
      totals.service_fees ??
        serviceFeeItems.reduce((sum, item) => sum + Number(item?.actual_price_per_unit || 0), 0)
    );
    const selectedTotal = Number(
      totals.total_selected ?? subscriptionTotal + hardwareTotal + serviceFeesTotal
    );

    return {
      subscriptionItems,
      hardwareItems,
      serviceFeeItems,
      additionalItems,
      subscriptionTotal,
      hardwareTotal,
      serviceFeesTotal,
      selectedTotal,
    };
  }, [parsedServiceData]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const items = serviceDataBreakdown.subscriptionItems || [];
      const subscriptionItemId = (item) =>
        Number(
          item?.sunscription_item_id ??
            item?.subscription_item_id ??
            item?.subscriptionItemId ??
            item?.id ??
            0
        );

      const ids = [...new Set(items.map(subscriptionItemId).filter((n) => n > 0))];
      if (!ids.length) {
        if (!cancelled) setRadiusPackageInfo(false);
        return;
      }
      try {
        const res = await api.post('/radius/subscription_items/radius_package_rates', {
          sunscription_item_ids: ids,
        });
        const links = Array.isArray(res?.links)
          ? res.links
          : Array.isArray(res)
            ? res
            : [];
        const rowSid = (row) =>
          Number(
            row?.sunscription_item_id ??
              row?.subscription_item_id ??
              row?.subscriptionItemId ??
              row?.id ??
              0
          );
        const byId = new Map(
          links
            .filter((row) => rowSid(row) > 0 && String(row?.rate_limit_value || '').trim())
            .map((row) => [rowSid(row), row])
        );
        const subscriptionSortRank = (item) => {
          const cat = String(item?.category_name || '').toLowerCase();
          const cid = Number(item?.category_id || 0);
          if (cid === 1 || cat.includes('internet') || cat.includes('package')) return 0;
          return 1;
        };
        const orderedItems = [...items].sort((a, b) => subscriptionSortRank(a) - subscriptionSortRank(b));
        let chosen = null;
        for (const item of orderedItems) {
          const sid = subscriptionItemId(item);
          if (sid < 1) continue;
          const link = byId.get(sid);
          if (link) {
            chosen = link;
            break;
          }
        }
        if (!chosen && links.length > 0) {
          chosen = links.find((l) => String(l?.rate_limit_value || '').trim()) || null;
        }
        if (!chosen || !String(chosen.rate_limit_value || '').trim()) {
          if (!cancelled) setRadiusPackageInfo(false);
          return;
        }
        const parsed = parseMikrotikRateLimit(chosen.rate_limit_value);
        if (!parsed) {
          if (!cancelled) setRadiusPackageInfo(false);
          return;
        }
        if (!cancelled) {
          setRadiusPackageInfo({
            groupname: String(chosen.groupname || '').trim(),
            throttleUlDl: parsed.throttleUlDl,
            speedUlDl: parsed.speedUlDl,
          });
        }
      } catch {
        if (!cancelled) setRadiusPackageInfo(false);
      }
    };
    if (!contractData) {
      setRadiusPackageInfo(null);
      return () => {
        cancelled = true;
      };
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [contractData, serviceDataBreakdown.subscriptionItems]);

  const contractCodeName = useMemo(() => {
    if (customerData && customerData.code && contractData && contractData.contract_id) {
      const paddedContractId = String(contractData.contract_id).padStart(5, '0');
      return `${customerData.code}-${paddedContractId}`;
    }
    return null;
  }, [customerData, contractData]);

  const displayedEndDate = useMemo(() => {
    if (scheduledTerminationDate) return formatDate(scheduledTerminationDate);
    const autoRenewEnabled = Number(contractData?.auto_renew || 0) === 1;
    if (!autoRenewEnabled) {
      const termEnd = getContractTermEndDateIso(contractData?.start_date, contractData?.contract_term);
      if (termEnd) return termEnd;
    }
    return formatDate(contractData?.end_date);
  }, [scheduledTerminationDate, contractData]);

  if (loading) {
    return wrapContractViewShell(
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
        <MDTypography sx={{ ml: 2 }}>Loading contract details...</MDTypography>
      </MDBox>
    );
  }

  if (error) {
    return wrapContractViewShell(
      <MDBox p={3}>
        <PageErrorAlert title={PAGE_ERROR_TITLES.contract} message={error} />
        <Button
          onClick={() => navigate(parentViewReturnUrl, parentViewReturnState ? { state: parentViewReturnState } : undefined)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </MDBox>
    );
  }

  if (!contractData) {
    return wrapContractViewShell(
      <MDBox p={3}>
        <Alert severity="warning">No contract data available.</Alert>
        <Button
          onClick={() => navigate(parentViewReturnUrl, parentViewReturnState ? { state: parentViewReturnState } : undefined)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </MDBox>
    );
  }

  return wrapContractViewShell(
      <MDBox py={3}>
        <Card>
          <MDBox
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            mx={2}
            mt={-3}
            p={3}
            mb={1}
            display="flex"
            alignItems="center"
            gap={2}
          >
            <MDButton 
              variant="text"
              color="white"
              onClick={() => navigate(parentViewReturnUrl, parentViewReturnState ? { state: parentViewReturnState } : undefined)}
              startIcon={<Icon>arrow_back</Icon>}
              sx={{ mr: 1 }}
            >
              Back
            </MDButton>
            <MDTypography variant="h6" color="white" sx={{ flexGrow: 1 }}>
              {contractCodeName 
                ? `Contract Code: ${contractCodeName}` 
                : `Contract Details`}
            </MDTypography>
            {!isClientPortal && (
            <GroupPolicyGate policy="contractManage">
            <MDButton
              variant="outlined"
              color="white"
              onClick={() => navigate(`/contracts/edit/${contractId}`, {
                state: {
                  contractData: contractData,
                  customerData: customerData,
                  returnToContractViewUrl: location.pathname,
                  parentViewReturnUrl: parentViewReturnUrl
                }
              })}
              startIcon={<Icon>edit</Icon>}
            >
              Edit Contract
            </MDButton>
            </GroupPolicyGate>
            )}
          </MDBox>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <ContractDetailTwoColumnLayout
              renderCustomer={() =>
                customerData ? (
                  <Box component="section">
                                    <MDTypography variant="h6" gutterBottom>Customer</MDTypography>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                      <List dense>
                                        <ListItem sx={{ pl:0, mb: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                          <ListItemText 
                                            primary="Customer Identifiers" 
                                            primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                          />
                                        </ListItem>
                                        <ListItem sx={{pl: 4, py: 0.5}}> 
                                          <ListItemText 
                                            primary="Name"
                                            secondary={customerData.name || 'N/A'} 
                                            primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                          />
                                        </ListItem>
                                        <ListItem sx={{pl: 4, py: 0.5}}>
                                          <ListItemText 
                                            primary="Customer Code"
                                            secondary={customerData.code || 'N/A'} 
                                            primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                          />
                                        </ListItem>
                                      </List>
                                    </Paper>
                                </Box>
                ) : null
              }
              renderGeneral={() => (
                <>
                {/* General Contract Details */}
                              <Box component="section">
                                <MDTypography variant="h6" gutterBottom>General Information</MDTypography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                  <List dense>
                                    {/* Contract Overview Section */}
                                    <ListItem sx={{ pl:0, mb: 0.5, borderLeft: '2px solid', borderColor: 'info.main' }}>
                                      <ListItemText 
                                        primary="Contract Overview" 
                                        primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                      />
                                    </ListItem>
                                    <ListItem sx={{pl: 4, py: 0.5}}>
                                      <ListItemText primary="Contract ID" secondary={contractData.contract_id || 'N/A'} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }} />
                                    </ListItem>
                                    <ListItem sx={{pl: 4, py: 0.5}}>
                                      <ListItemText primary="Service Plan" secondary={contractData.service_plan || 'N/A'} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }} />
                                    </ListItem>
                                    <ListItem sx={{pl: 4, py: 0.5}}>
                                      <ListItemText primary="Contract Type" secondary={contractData.contract_type || 'N/A'} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }} />
                                    </ListItem>
                                    <ListItem sx={{pl: 4, py: 0.5}}>
                                      <ListItemText
                                        primary="Status"
                                        secondary={
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            <MDBadge
                                              badgeContent={contractData.status || "N/A"}
                                              color={CONTRACT_STATUS_COLORS[contractData.status] || "light"}
                                              size="xs"
                                              container
                                            />
                                            {hasTerminationSchedule ? (
                                              <MDBadge
                                                badgeContent="termination scheduled"
                                                color="warning"
                                                size="xs"
                                                container
                                              />
                                            ) : null}
                                          </Box>
                                        }
                                        primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                        secondaryTypographyProps={{ component: "div" }}
                                      />
                                    </ListItem>
                
                                    {/* Timeline & Term Section */}
                                    <ListItem sx={{ pl:0, mt: 2, mb: 0.5, borderLeft: '2px solid', borderColor: 'success.main' }}>
                                      <ListItemText 
                                        primary="Timeline & Term" 
                                        primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                      />
                                    </ListItem>
                                    <ListItem sx={{pl: 4, py: 0.5}}>
                                      <ListItemText primary="Start Date" secondary={formatDate(contractData.start_date)} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }} />
                                    </ListItem>
                                    <ListItem sx={{pl: 4, py: 0.5}}>
                                      <ListItemText primary="End Date" secondary={displayedEndDate} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }} />
                                    </ListItem>
                                    <ListItem sx={{pl: 4, py: 0.5}}>
                                      <ListItemText primary="Contract Term" secondary={contractData.contract_term ? `${contractData.contract_term} months` : 'N/A'} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }} />
                                    </ListItem>
                                    
                                    {/* Billing & Renewal Section */}
                                    <ListItem sx={{ pl:0, mt: 2, mb: 0.5, borderLeft: '2px solid', borderColor: 'warning.main' }}>
                                      <ListItemText 
                                        primary="Billing & Renewal" 
                                        primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                      />
                                    </ListItem>
                                    <ListItem sx={{pl: 4, py: 0.5}}>
                                      <ListItemText primary="Billing Frequency" secondary={contractData.billing_frequency || 'N/A'} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }}/>
                                    </ListItem>
                                    <ListItem sx={{pl: 4, py: 0.5}}>
                                      <ListItemText primary="Auto Renew" secondary={contractData.auto_renew ? 'Yes' : 'No'} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }}/>
                                    </ListItem>
                                    {parsedBillingData && (
                                      <>
                                        <ListItem sx={{pl: 4, py: 0.5}}>
                                          <ListItemText primary="Invoice Day of Month" secondary={parsedBillingData.invoice_day_of_month !== undefined ? parsedBillingData.invoice_day_of_month : 'N/A'} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }}/>
                                        </ListItem>
                                        <ListItem sx={{pl: 4, py: 0.5}}>
                                          <ListItemText primary="Due Date Offset Days" secondary={parsedBillingData.due_date_offset_days !== undefined ? parsedBillingData.due_date_offset_days : 'N/A'} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }}/>
                                        </ListItem>
                                        <ListItem sx={{pl: 4, py: 0.5}}>
                                          <ListItemText primary="Source Next Run Date" secondary={parsedBillingData.source_next_run_date ? formatDate(parsedBillingData.source_next_run_date) : 'N/A'} primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }} secondaryTypographyProps={{ variant: 'caption' }}/>
                                        </ListItem>
                                      </>
                                    )}
                                  </List>
                                </Paper>
                              </Box>
                </>
              )}
              renderService={() => (
                <>
                {/* Service Information */}
                              <Box component="section">
                                <MDTypography variant="h6" gutterBottom>Service Information</MDTypography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                  {parsedServiceData || contractData.service_plan ? (
                                    <List dense>
                                      <ListItem sx={{ pl:0, mb: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                        <ListItemText 
                                          primary="Service Plan Reference" 
                                          primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                        />
                                      </ListItem>
                                      <ListItem sx={{pl: 4, py: 0.5}}>
                                         <ListItemText 
                                          primary="Plan Name (from contract)"
                                          secondary={contractData.service_plan || 'N/A'}
                                          primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                          secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                      </ListItem>
                
                                      {parsedServiceData && (
                                         <>
                                          <ListItem sx={{ pl:0, mt: 2, mb: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                            <ListItemText 
                                              primary="Service Details" 
                                              primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                            />
                                          </ListItem>
                                          <ListItem sx={{pl: 4, py: 0.5}}>
                                            <ListItemText 
                                              primary="Service Type"
                                              secondary={parsedServiceData.serviceType || 'N/A'}
                                              primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                              secondaryTypographyProps={{ variant: 'caption' }}
                                            />
                                          </ListItem>
                                          <ListItem sx={{pl: 4, py: 0.5}}>
                                            <ListItemText 
                                              primary="Package Type"
                                              secondary={parsedServiceData.packageType || 'N/A'}
                                              primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                              secondaryTypographyProps={{ variant: 'caption' }}
                                            />
                                          </ListItem>
                
                                          {!parsedServiceData.plan &&
                                            radiusPackageInfo &&
                                            typeof radiusPackageInfo === 'object' && (
                                              <>
                                                <ListItem sx={{ pl: 0, mt: 2, mb: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                                  <ListItemText
                                                    primary="RADIUS line rates"
                                                    primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: { ml: 2 } }}
                                                  />
                                                </ListItem>
                                                <ListItem sx={{ pl: 4, py: 0.5 }}>
                                                  <ListItemText
                                                    primary="Profile (group)"
                                                    secondary={radiusPackageInfo.groupname || '—'}
                                                    primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                  />
                                                </ListItem>
                                                <ListItem sx={{ pl: 4, py: 0.5 }}>
                                                  <ListItemText
                                                    primary="Throttle (UL / DL)"
                                                    secondary={formatMikrotikUlDlMbps(radiusPackageInfo.throttleUlDl)}
                                                    primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                  />
                                                </ListItem>
                                                <ListItem sx={{ pl: 4, py: 0.5 }}>
                                                  <ListItemText
                                                    primary="Speed (UL / DL)"
                                                    secondary={formatMikrotikUlDlMbps(radiusPackageInfo.speedUlDl)}
                                                    primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                  />
                                                </ListItem>
                                              </>
                                            )}
                                          
                                          {parsedServiceData.plan && (
                                            <>
                                              <ListItem sx={{ pl:0, mt: 2, mb: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                                <ListItemText 
                                                  primary="Plan Specifications" 
                                                  primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                                />
                                              </ListItem>
                                              {Object.entries(parsedServiceData.plan)
                                                .filter(
                                                  ([key]) =>
                                                    !INTERNAL_ID_KEYS.has(key) && !PLAN_SUPPRESS_KEYS.has(key)
                                                )
                                                .map(([key, value]) => (
                                                <ListItem key={key} sx={{pl: 4, py: 0.5}}>
                                                  <ListItemText 
                                                    primary={toLabel(key)}
                                                    secondary={formatFieldValue(key, value)} 
                                                    primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                  />
                                                </ListItem>
                                              ))}
                                              {radiusPackageInfo && typeof radiusPackageInfo === 'object' && (
                                                <>
                                                  <ListItem sx={{ pl: 4, py: 0.5 }}>
                                                    <ListItemText
                                                      primary="Throttle (UL / DL)"
                                                      secondary={formatMikrotikUlDlMbps(radiusPackageInfo.throttleUlDl)}
                                                      primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                      secondaryTypographyProps={{ variant: 'caption' }}
                                                    />
                                                  </ListItem>
                                                  <ListItem sx={{ pl: 4, py: 0.5 }}>
                                                    <ListItemText
                                                      primary="Speed (UL / DL)"
                                                      secondary={formatMikrotikUlDlMbps(radiusPackageInfo.speedUlDl)}
                                                      primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                      secondaryTypographyProps={{ variant: 'caption' }}
                                                    />
                                                  </ListItem>
                                                </>
                                              )}
                                            </>
                                          )}
                                          <ListItem sx={{ pl:0, mt: 2, mb: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                            <ListItemText 
                                              primary="Subscription Items" 
                                              primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                            />
                                          </ListItem>
                                          {serviceDataBreakdown.subscriptionItems.length > 0 ? (
                                            serviceDataBreakdown.subscriptionItems.map((item, index) => (
                                              <ListItem key={`subscription-item-${item?.sunscription_item_id || index}`} sx={{pl: 4, py: 0.5}}>
                                                <ListItemText
                                                  primary={`${item?.code || 'N/A'} - ${item?.description || item?.name || 'Item'}`}
                                                  secondary={`R ${Number(item?.actual_price_per_unit || 0).toFixed(2)}`}
                                                  primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                  secondaryTypographyProps={{ variant: 'caption' }}
                                                />
                                              </ListItem>
                                            ))
                                          ) : (
                                            <ListItem sx={{pl: 4, py: 0.5}}>
                                              <ListItemText
                                                primary="No subscription items"
                                                primaryTypographyProps={{ variant: 'caption' }}
                                              />
                                            </ListItem>
                                          )}
                
                                          {radiusPackageInfo && typeof radiusPackageInfo === 'object' && (
                                            <>
                                              <ListItem
                                                sx={{
                                                  pl: 0,
                                                  mt: 2,
                                                  mb: 0.5,
                                                  borderLeft: '2px solid',
                                                  borderColor: 'divider',
                                                }}
                                              >
                                                <ListItemText
                                                  primary="RADIUS package profile"
                                                  primaryTypographyProps={{
                                                    variant: 'subtitle2',
                                                    fontWeight: 'medium',
                                                    sx: { ml: 2 },
                                                  }}
                                                />
                                              </ListItem>
                                              <ListItem sx={{ pl: 4, py: 0.5 }}>
                                                <ListItemText
                                                  primary="Profile (group)"
                                                  secondary={radiusPackageInfo.groupname || '—'}
                                                  primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                  secondaryTypographyProps={{ variant: 'caption' }}
                                                />
                                              </ListItem>
                                            </>
                                          )}
                
                                          <ListItem sx={{ pl:0, mt: 2, mb: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                            <ListItemText 
                                              primary="Hardware Items" 
                                              primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                            />
                                          </ListItem>
                                          {serviceDataBreakdown.hardwareItems.length > 0 ? (
                                            serviceDataBreakdown.hardwareItems.map((item, index) => (
                                              <ListItem key={`hardware-item-${item?.sunscription_item_id || index}`} sx={{pl: 4, py: 0.5}}>
                                                <ListItemText
                                                  primary={`${item?.code || 'N/A'} - ${item?.description || item?.name || 'Item'}`}
                                                  secondary={`R ${Number(item?.actual_price_per_unit || 0).toFixed(2)}`}
                                                  primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                  secondaryTypographyProps={{ variant: 'caption' }}
                                                />
                                              </ListItem>
                                            ))
                                          ) : (
                                            <ListItem sx={{pl: 4, py: 0.5}}>
                                              <ListItemText
                                                primary="No hardware items"
                                                primaryTypographyProps={{ variant: 'caption' }}
                                              />
                                            </ListItem>
                                          )}
                
                                          <ListItem sx={{ pl:0, mt: 2, mb: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                            <ListItemText 
                                              primary="Service Fee Items" 
                                              primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                            />
                                          </ListItem>
                                          {serviceDataBreakdown.serviceFeeItems.length > 0 ? (
                                            serviceDataBreakdown.serviceFeeItems.map((item, index) => (
                                              <ListItem key={`service-fee-item-${item?.sunscription_item_id || index}`} sx={{pl: 4, py: 0.5}}>
                                                <ListItemText
                                                  primary={`${item?.code || 'N/A'} - ${item?.description || item?.name || 'Item'}`}
                                                  secondary={`R ${Number(item?.actual_price_per_unit || 0).toFixed(2)}`}
                                                  primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                  secondaryTypographyProps={{ variant: 'caption' }}
                                                />
                                              </ListItem>
                                            ))
                                          ) : (
                                            <ListItem sx={{pl: 4, py: 0.5}}>
                                              <ListItemText
                                                primary="No service fee items"
                                                primaryTypographyProps={{ variant: 'caption' }}
                                              />
                                            </ListItem>
                                          )}
                                         </>
                                      )}
                                      <ListItem sx={{ pl:0, mt: 2, mb: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                        <ListItemText 
                                          primary="Associated Fees" 
                                          primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium', sx: {ml: 2} }}
                                        />
                                      </ListItem>
                                      <ListItem sx={{pl: 4, py: 0.5}}>
                                        <ListItemText 
                                          primary="Subscription Total" 
                                          secondary={`R ${Number(serviceDataBreakdown.subscriptionTotal || contractData.monthly_fee || 0).toFixed(2)}`} 
                                          primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                          secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                      </ListItem>
                                      <ListItem sx={{pl: 4, py: 0.5}}>
                                        <ListItemText 
                                          primary="Hardware Total" 
                                          secondary={`R ${Number(serviceDataBreakdown.hardwareTotal || 0).toFixed(2)}`} 
                                          primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                          secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                      </ListItem>
                                      <ListItem sx={{pl: 4, py: 0.5}}>
                                        <ListItemText 
                                          primary="Service Fees Total" 
                                          secondary={`R ${Number(serviceDataBreakdown.serviceFeesTotal || contractData.installation_fee || 0).toFixed(2)}`} 
                                          primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                          secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                      </ListItem>
                                      <ListItem sx={{pl: 4, py: 0.5}}>
                                        <ListItemText 
                                          primary="Total Selected Value" 
                                          secondary={`R ${Number(serviceDataBreakdown.selectedTotal || 0).toFixed(2)}`} 
                                          primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                          secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                      </ListItem>
                                    </List>
                                  ) : (
                                    <MDTypography variant="body2">No detailed service information available.</MDTypography>
                                  )}
                                </Paper>
                              </Box>
                </>
              )}
              renderEquipment={() => (
                <>
                {/* Equipment Information */}
                              <Box component="section">
                                <MDTypography variant="h6" gutterBottom>Equipment Information</MDTypography>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                  {parsedEquipmentData ? (
                                    <List dense>
                                      <ListItem sx={{pl:0, py: 0.5}}>
                                        <ListItemText 
                                          primary="Purchase Option" 
                                          secondary={parsedEquipmentData.purchaseOption || 'N/A'} 
                                          primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium'}}
                                          secondaryTypographyProps={{ variant: 'caption'}}
                                        />
                                      </ListItem>
                                      {serviceDataBreakdown.hardwareItems && serviceDataBreakdown.hardwareItems.length > 0 && (
                                        <>
                                          <ListItem sx={{pl:0, mt:1, mb: -0.5}}>
                                            <ListItemText 
                                              primary="Hardware Items" 
                                              primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium'}}
                                            />
                                          </ListItem>
                                          {serviceDataBreakdown.hardwareItems.map((item, index) => (
                                            <React.Fragment key={item.id || index}>
                                              <ListItem sx={{ pl:2, mt: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                                <ListItemText 
                                                  primary={item.name || `Item ${index + 1}`} 
                                                  primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold'}}
                                                />
                                              </ListItem>
                                              {Object.entries(item).map(([key, value]) => {
                                                if (ITEM_HIDDEN_KEYS.has(key)) return null;
                                                return (
                                                  <ListItem key={key} sx={{pl: 4, py: 0.5}}>
                                                    <ListItemText 
                                                      primary={toLabel(key)}
                                                      secondary={formatFieldValue(key, value)} 
                                                      primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                      secondaryTypographyProps={{ variant: 'caption' }}
                                                    />
                                                  </ListItem>
                                                );
                                              })}
                                            </React.Fragment>
                                          ))}
                                        </>
                                      )}
                                      {(!serviceDataBreakdown.hardwareItems || serviceDataBreakdown.hardwareItems.length === 0) &&
                                        parsedEquipmentData.items &&
                                        parsedEquipmentData.items.length > 0 && (
                                        <>
                                          <ListItem sx={{pl:0, mt:1, mb: -0.5}}>
                                            <ListItemText 
                                              primary="Equipment Items" 
                                              primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium'}}
                                            />
                                          </ListItem>
                                          {parsedEquipmentData.items.map((item, index) => (
                                            <React.Fragment key={item.id || index}>
                                              <ListItem sx={{ pl:2, mt: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                                                <ListItemText 
                                                  primary={item.name || `Item ${index + 1}`} 
                                                  primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold'}}
                                                />
                                              </ListItem>
                                              {Object.entries(item).map(([key, value]) => {
                                                if (ITEM_HIDDEN_KEYS.has(key)) return null;
                                                return (
                                                  <ListItem key={key} sx={{pl: 4, py: 0.5}}>
                                                    <ListItemText 
                                                      primary={toLabel(key)}
                                                      secondary={formatFieldValue(key, value)} 
                                                      primaryTypographyProps={{ variant: 'caption', fontWeight: 'medium' }}
                                                      secondaryTypographyProps={{ variant: 'caption' }}
                                                    />
                                                  </ListItem>
                                                );
                                              })}
                                            </React.Fragment>
                                          ))}
                                        </>
                                      )}
                                    </List>
                                  ) : (
                                    <MDTypography variant="body2">No detailed equipment information available.</MDTypography>
                                  )}
                                </Paper>
                              </Box>
                </>
              )}
            />
            <Grid container spacing={3} alignItems="flex-start">
              {/* Notes and Special Terms */}
              <Grid item xs={12}>
                <MDTypography variant="h6" gutterBottom>Notes & Special Terms</MDTypography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <MDTypography variant="subtitle2" fontWeight="medium">Notes</MDTypography>
                  <MDTypography variant="body2" color="text" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                    {contractData.notes || 'No notes provided.'}
                  </MDTypography>
                  <Divider sx={{ my:1 }} />
                  <MDTypography variant="subtitle2" fontWeight="medium">Special Terms</MDTypography>
                  <MDTypography variant="body2" color="text" sx={{ whiteSpace: 'pre-wrap' }}>
                    {contractData.special_terms || 'No special terms specified.'}
                  </MDTypography>
                </Paper>
              </Grid>
              
              {/* Cost Summary Section */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, mt: 3, mb:3 }}>
                  <MDTypography variant="h6" gutterBottom>
                    Contract Financial Summary
                  </MDTypography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <MDTypography variant="subtitle2">Subscription Total:</MDTypography>
                      <MDTypography variant="h6" color="info">
                        R {Number(serviceDataBreakdown.subscriptionTotal || contractData.monthly_fee || 0).toFixed(2)}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MDTypography variant="subtitle2">Hardware Total:</MDTypography>
                      <MDTypography variant="h6" color="info">
                        R {Number(serviceDataBreakdown.hardwareTotal || 0).toFixed(2)}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        (Once-off)
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MDTypography variant="subtitle2">Service Fees Total:</MDTypography>
                      <MDTypography variant="h6" color="info">
                        R {Number(serviceDataBreakdown.serviceFeesTotal || contractData.installation_fee || 0).toFixed(2)}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        (Once-off)
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <MDTypography variant="subtitle2">Total Selected Value:</MDTypography>
                      <MDTypography variant="h6" color="dark">
                        R {Number(serviceDataBreakdown.selectedTotal || 0).toFixed(2)}
                      </MDTypography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Signature display (if present) */}
              {contractData.signature && (
                <Grid item xs={12}>
                   <MDTypography variant="h6" gutterBottom>Terms Acceptance Signature</MDTypography>
                   <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                     <img 
                        src={contractData.signature} 
                        alt="Contract Signature" 
                        style={{ maxWidth: '100%', maxHeight: '300px', border: '1px solid #ddd' }} 
                     />
                   </Paper>
                </Grid>
              )}

            </Grid>
          </CardContent>
        </Card>
      </MDBox>
  );
}

ContractView.propTypes = {
  portalVariant: PropTypes.oneOf(['admin', 'client']),
  expectedCustomerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  clientAccessToken: PropTypes.string,
};

export default ContractView; 